<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { renderMarkdownWithCitations, renderAnswerForCopy, renderAnswerForPrint } from '@/lib/markdown'
import { parseCitationsFromMarkdown } from '@/lib/citations'
import { useCitations } from '@/composables/useCitations'
import { getRiskColor, getRiskTextColor, getRiskLabel } from '@/lib/riskTier'
import { extractCharts, stripChartSpecsForDisplay, replaceChartSpecsWithTables } from '@/lib/charts'
import { alignBarChartLabelColumns } from '@/lib/chartAlignment'
import { printAnswer, serializeChartSvg } from '@/lib/print'
import HighchartsChart from './HighchartsChart.vue'
import type { ChatMessage, Citation } from '@/types'

const props = defineProps<{ message: ChatMessage }>()
const emit = defineEmits<{
  (e: 'retry'): void
  (e: 'feedback', payload: { message: ChatMessage; sentiment: 'up' | 'down' }): void
}>()

const { setActiveCitations, focusCitation, openHover, scheduleClose, cancelClose } = useCitations()

const isUser = computed(() => props.message.role === 'user')
const isError = computed(() => !isUser.value && props.message.error === true)

// MCP / probe-derived references have no real URL — only render a clickable
// link for genuine http(s) sources; otherwise show plain (non-linked) text.
function isLinkable(url?: string | null): boolean {
  return !!url && /^https?:\/\//i.test(url.trim())
}

// Backend embeds analytics as fenced ```chartspec blocks (chart config + table).
// The bubble shows the interactive chart only; the paired table is reserved for
// copy/paste (see copyToClipboard). Align category columns so stacked bar charts
// share one label gutter (otherwise each chart auto-sizes independently).
const charts = computed(() => {
  if (isUser.value || isError.value) return []
  return alignBarChartLabelColumns(extractCharts(props.message.content || ''))
})

const displayContent = computed(() => {
  if (isUser.value || isError.value) return props.message.content || ''
  return stripChartSpecsForDisplay(props.message.content || '')
})

const citations = computed<Citation[]>(() => {
  if (isUser.value || isError.value) return []
  return parseCitationsFromMarkdown(displayContent.value, props.message.citations)
})

const html = computed(() => {
  if (isUser.value || isError.value) return ''
  return renderMarkdownWithCitations(displayContent.value, citations.value)
})

const contentEl = ref<HTMLElement | null>(null)
const mobileListEl = ref<HTMLElement | null>(null)
const chartsEl = ref<HTMLElement | null>(null)

// Keep the shared reference pane pointed at this answer's citations. Bubbles
// mount/update in order, so the latest answer naturally wins while streaming;
// clicking a bubble (or an older marker) re-points the pane to that answer.
watch(citations, (list) => {
  if (list.length) setActiveCitations(list)
}, { immediate: true })

// Clicking anywhere in an assistant answer makes its references the active set
// in the reference pane (so multiple answers in a thread are addressable).
function onBubbleClick() {
  if (isUser.value || isError.value) return
  setActiveCitations(citations.value)
}

// Explicit "References" affordance: re-points the reference pane at this
// answer (desktop) and, on mobile where the pane is hidden, scrolls to the
// inline reference list at the bottom of the answer.
function showReferences() {
  if (!citations.value.length) return
  setActiveCitations(citations.value)
  if (isMobile()) {
    mobileListEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
}

function findCitation(id: number): Citation | undefined {
  return citations.value.find((c) => c.id === id)
}

function markerFrom(target: EventTarget | null): { el: HTMLElement; citation: Citation } | null {
  if (!(target instanceof Element)) return null
  const el = target.closest<HTMLElement>('.citation-marker')
  if (!el) return null
  const id = Number(el.getAttribute('data-cite-id'))
  const citation = findCitation(id)
  if (!citation) return null
  return { el, citation }
}

function anchorRect(el: HTMLElement) {
  const r = el.getBoundingClientRect()
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right }
}

function onContentPointerOver(e: PointerEvent | MouseEvent) {
  const hit = markerFrom(e.target)
  if (!hit) return
  cancelClose()
  openHover({ citation: hit.citation, anchor: anchorRect(hit.el) })
}

function onContentPointerOut(e: PointerEvent | MouseEvent) {
  if (!markerFrom(e.target)) return
  scheduleClose()
}

function onContentFocusIn(e: FocusEvent) {
  const hit = markerFrom(e.target)
  if (!hit) return
  cancelClose()
  openHover({ citation: hit.citation, anchor: anchorRect(hit.el) })
}

function onContentFocusOut(e: FocusEvent) {
  if (!markerFrom(e.target)) return
  scheduleClose()
}

function onContentClick(e: MouseEvent) {
  const hit = markerFrom(e.target)
  if (!hit) return
  e.preventDefault()
  if (isMobile()) {
    jumpToEntry(hit.citation.id)
  } else {
    openHover({ citation: hit.citation, anchor: anchorRect(hit.el), pinned: true })
    focusCitation(hit.citation.id, citations.value)
  }
}

function jumpToEntry(id: number) {
  const el = mobileListEl.value?.querySelector<HTMLElement>(`[data-ref-id="${id}"]`)
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

function jumpToMarker(id: number) {
  const el = contentEl.value?.querySelector<HTMLElement>(`.citation-marker[data-cite-id="${id}"]`)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.focus()
  }
}

let cleanup: (() => void) | null = null
onMounted(() => {
  const root = contentEl.value
  if (!root) return
  const over = (e: Event) => onContentPointerOver(e as MouseEvent)
  const out = (e: Event) => onContentPointerOut(e as MouseEvent)
  const click = (e: Event) => onContentClick(e as MouseEvent)
  const focusIn = (e: Event) => onContentFocusIn(e as FocusEvent)
  const focusOut = (e: Event) => onContentFocusOut(e as FocusEvent)
  root.addEventListener('mouseover', over)
  root.addEventListener('mouseout', out)
  root.addEventListener('click', click)
  root.addEventListener('focusin', focusIn)
  root.addEventListener('focusout', focusOut)
  cleanup = () => {
    root.removeEventListener('mouseover', over)
    root.removeEventListener('mouseout', out)
    root.removeEventListener('click', click)
    root.removeEventListener('focusin', focusIn)
    root.removeEventListener('focusout', focusOut)
  }
})
onBeforeUnmount(() => cleanup?.())

// Show the actions toolbar only on real assistant answers (not errors, empty
// bubbles, or questions back to the user).
const showActions = computed(
  () => !isUser.value && !isError.value && !props.message.awaitingReply && !!props.message.content?.trim(),
)

const copied = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | null = null

async function copyToClipboard() {
  // Bracketed [n] inline markers + an appended numbered References list, in both
  // rich HTML (with best-effort in-document links) and plain text.
  // For copy we swap each chart for its markdown table (charts don't paste).
  const copySource = replaceChartSpecsWithTables(props.message.content || '')
  const { html: copyHtml, text } = renderAnswerForCopy(copySource, citations.value)
  try {
    // Write both rich HTML and plain text so pasting into Word/Docs keeps
    // headings, bold, lists and tables, while plain-text targets still work.
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([copyHtml], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ])
    } else {
      await navigator.clipboard.writeText(text)
    }
    copied.value = true
    if (copiedTimer) clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => { copied.value = false }, 1800)
  } catch (e) {
    console.error('[MessageBubble] copy failed:', e)
  }
}

// Print a self-contained, branded version of this answer (text + graphs +
// references + AI disclaimer) via a hidden iframe. Charts are captured as the
// already-rendered SVGs; chart fences are stripped from the text body.
function printMessage() {
  const bodyHtml = renderAnswerForPrint(
    stripChartSpecsForDisplay(props.message.content || ''),
    citations.value,
  )
  const chartSvgs = chartsEl.value
    ? Array.from(chartsEl.value.querySelectorAll('svg')).map((s) =>
        serializeChartSvg(s as unknown as SVGElement),
      )
    : []
  printAnswer({ bodyHtml, chartSvgs, citations: citations.value })
}
</script>

<template>
  <!-- Customer-facing error notice -->
  <div
    v-if="isError"
    class="self-start max-w-[85%] flex items-start gap-2.5 px-4 py-3 rounded-[14px_14px_14px_4px] border border-amber-200 bg-amber-50 text-amber-900 text-sm leading-relaxed"
    role="alert"
  >
    <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
    </svg>
    <div class="flex-1">
      <p class="whitespace-pre-line">{{ message.content }}</p>
      <button
        v-if="message.retryable !== false"
        type="button"
        class="mt-2 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100"
        @click="emit('retry')"
      >
        Try again
      </button>
    </div>
  </div>

  <!-- Normal user / assistant bubble -->
  <div
    v-else
    class="max-w-[85%] text-sm leading-relaxed"
    :class="isUser
      ? 'self-end bg-blue-600 px-3.5 py-2.5 rounded-[14px_14px_4px_14px] text-white'
      : 'self-start bg-white px-4 py-3 rounded-[14px_14px_14px_4px] border border-neutral-200 text-neutral-700'"
    @click="onBubbleClick"
  >
    <template v-if="isUser">{{ message.content }}</template>
    <template v-else>
      <div
        ref="contentEl"
        class="markdown-content"
        v-html="html"
      />

      <!-- Interactive analytics charts (Highcharts) extracted from the answer. -->
      <div v-if="charts.length" ref="chartsEl" class="mt-3 space-y-4">
        <div
          v-for="(opt, i) in charts"
          :key="i"
          class="rounded-lg border border-neutral-200 bg-neutral-50/60 p-2"
        >
          <HighchartsChart :options="opt" />
        </div>
      </div>

      <!-- Mobile reference list: shown at the bottom of the answer; markers
           jump here and each entry links back to its marker. -->
      <ol
        v-if="citations.length"
        ref="mobileListEl"
        class="md:hidden mt-3 border-t border-neutral-100 pt-2.5 space-y-1.5"
      >
        <li class="text-[11px] uppercase tracking-wider font-semibold text-neutral-400 list-none">
          References
        </li>
        <li
          v-for="c in citations"
          :key="c.id"
          :data-ref-id="c.id"
          class="flex gap-2 list-none"
        >
          <span class="flex items-center gap-1 shrink-0 pt-0.5">
            <!-- Always show the tier dot (N/A grey when unset) for column alignment. -->
            <span class="inline-block h-2 w-2 rounded-full" :style="{ backgroundColor: getRiskColor(c.riskTier) }" aria-hidden="true" />
            <span class="text-[12px] font-semibold tabular-nums text-neutral-500">{{ c.id }}</span>
          </span>
          <div class="min-w-0 flex-1">
            <span v-if="c.riskTier != null" class="text-[11px] font-medium" :style="{ color: getRiskTextColor(c.riskTier) }">{{ getRiskLabel(c.riskTier) }}</span>
            <div class="text-[13px] font-medium text-neutral-800 leading-tight">
              <a v-if="isLinkable(c.url)" :href="c.url" target="_blank" rel="noopener noreferrer" class="hover:text-blue-600 hover:underline">{{ c.title }}</a>
              <span v-else>{{ c.title }}</span>
            </div>
            <div class="flex items-center gap-2">
              <button type="button" class="text-[11px] text-blue-600 hover:underline shrink-0" @click="jumpToMarker(c.id)">
                back to text
              </button>
            </div>
          </div>
        </li>
      </ol>

      <!-- Assistant actions: copy + satisfaction feedback -->
      <div
        v-if="showActions"
        class="mt-2.5 -mb-0.5 flex items-center gap-1 border-t border-neutral-100 pt-2 text-neutral-400"
      >
        <button
          v-if="citations.length"
          type="button"
          class="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          :title="`Show ${citations.length} reference${citations.length === 1 ? '' : 's'}`"
          @click.stop="showReferences"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
            <rect x="3" y="4" width="14" height="12" rx="1.5" />
            <path d="M12.5 4v12" stroke-linecap="round" />
            <path d="M5.5 7.5h4M5.5 10h4" stroke-linecap="round" />
          </svg>
          References ({{ citations.length }})
        </button>

        <span v-if="citations.length" class="mx-0.5 h-3.5 w-px bg-neutral-200" aria-hidden="true" />

        <button
          type="button"
          class="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          title="Print response"
          @click="printMessage"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
            <path d="M6 7V3h8v4" stroke-linecap="round" stroke-linejoin="round" />
            <rect x="4" y="7" width="12" height="6" rx="1" />
            <path d="M6 13h8v4H6z" stroke-linejoin="round" />
          </svg>
          Print
        </button>

        <span class="mx-0.5 h-3.5 w-px bg-neutral-200" aria-hidden="true" />

        <button
          type="button"
          class="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          :title="copied ? 'Copied' : 'Copy response'"
          @click="copyToClipboard"
        >
          <svg v-if="!copied" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
            <rect x="7" y="7" width="9" height="9" rx="1.5" />
            <path d="M13 7V5.5A1.5 1.5 0 0011.5 4h-6A1.5 1.5 0 004 5.5v6A1.5 1.5 0 005.5 13H7" />
          </svg>
          <svg v-else class="h-3.5 w-3.5 text-green-600" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
            <path d="M4 10.5l4 4 8-9" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ copied ? 'Copied' : 'Copy' }}
        </button>

        <span class="mx-0.5 h-3.5 w-px bg-neutral-200" aria-hidden="true" />

        <button
          type="button"
          class="inline-flex items-center rounded px-1.5 py-1 transition-colors hover:bg-neutral-100"
          :class="message.feedback === 'up' ? 'text-green-600' : 'hover:text-neutral-600'"
          :title="'Good response'"
          :aria-pressed="message.feedback === 'up'"
          @click="emit('feedback', { message, sentiment: 'up' })"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" :fill="message.feedback === 'up' ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
            <path d="M6 9v7H3.5A1.5 1.5 0 012 14.5v-4A1.5 1.5 0 013.5 9H6zm0 0l3.2-6.2a1.5 1.5 0 012.8.7V6h3.4a1.5 1.5 0 011.48 1.76l-1 5.5A1.5 1.5 0 0114.9 16H6" stroke-linejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          class="inline-flex items-center rounded px-1.5 py-1 transition-colors hover:bg-neutral-100"
          :class="message.feedback === 'down' ? 'text-red-600' : 'hover:text-neutral-600'"
          :title="'Bad response'"
          :aria-pressed="message.feedback === 'down'"
          @click="emit('feedback', { message, sentiment: 'down' })"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" :fill="message.feedback === 'down' ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
            <path d="M14 11V4h2.5A1.5 1.5 0 0118 5.5v4A1.5 1.5 0 0116.5 11H14zm0 0l-3.2 6.2a1.5 1.5 0 01-2.8-.7V14H4.6a1.5 1.5 0 01-1.48-1.76l1-5.5A1.5 1.5 0 015.1 4H14" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>
